import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const TourContext = createContext(null);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

export const TourProvider = ({ children }) => {
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenTour, setHasSeenTour] = useState(true); // Default true to prevent flash

  useEffect(() => {
    // Check if user has seen the tour
    const tourCompleted = localStorage.getItem('estateprofiler_tour_completed');
    setHasSeenTour(!!tourCompleted);
  }, []);

  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsTourActive(true);
  }, []);

  const endTour = useCallback((markComplete = true) => {
    setIsTourActive(false);
    setCurrentStep(0);
    if (markComplete) {
      localStorage.setItem('estateprofiler_tour_completed', 'true');
      setHasSeenTour(true);
    }
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem('estateprofiler_tour_completed');
    setHasSeenTour(false);
  }, []);

  const value = {
    isTourActive,
    currentStep,
    hasSeenTour,
    startTour,
    endTour,
    nextStep,
    prevStep,
    goToStep,
    resetTour,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export default TourProvider;
